import { useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  useStartLiveAssistMutation,
  useStopLiveAssistMutation,
  useGetLiveCuecardsQuery,
  useGetLiveAssistStatusQuery,
} from "../api/apiSlice";

// Poll fast so a surfaced card shows within a second of being spoken. The backend
// matches keywords against partial transcripts and serves getLiveCuecards from
// memory, so the poll interval — not transcription — is what bounds the delay here.
const POLL_INTERVAL_MS = 1000;
// A displayed cue card auto-closes this long after it was (re)triggered.
const AUTO_CLOSE_MS = 30000;
// Identity of a single trigger: same card + same trigger time. When the keyword
// is spoken again the backend sends a NEW triggeredAt, so the key changes and the
// card is treated as a fresh trigger (un-dismissed, timer reset).
export const cardKey = (c) => `${c.id}:${c.triggeredAt}`;

/**
 * Owns the live-assist session (Recall.ai bot start/stop) and the live cue cards
 * it surfaces. Call this ONCE (in <App />) and share the result, so the header
 * Start/Stop button and the in-tab cue-card list drive the same session.
 */
export function useLiveAssist() {
  const meetingId = useSelector((s) => s.meeting.meetingId);
  const organizerId = useSelector((s) => s.meeting.organizerId);
  const joinUrl = useSelector((s) => s.meeting.joinUrl);

  const [started, setStarted] = useState(false);

  // Restore the "live" state if a bot is already in the meeting (reload/reopen).
  const { data: status } = useGetLiveAssistStatusQuery(
    { meetingId },
    { skip: !meetingId, refetchOnMountOrArgChange: true }
  );
  useEffect(() => {
    if (status?.active) {
      setStarted(true);
    }
  }, [status?.active]);

  const [startLiveAssist, { isLoading: starting, error: startError }] =
    useStartLiveAssistMutation();
  const [stopLiveAssist, { isLoading: stopping }] = useStopLiveAssistMutation();

  const { data: liveCards = [] } = useGetLiveCuecardsQuery(
    { meetingId },
    {
      skip: !started || !meetingId,
      pollingInterval: POLL_INTERVAL_MS,
      refetchOnMountOrArgChange: true,
    }
  );

  // --- Dismiss + 30s auto-close -------------------------------------------
  // dismissed: keys the user has closed by hand (stay hidden until re-trigger).
  const [dismissed, setDismissed] = useState(() => new Set());
  // seenAt: client-clock time we FIRST saw each trigger key. Drives the 30s
  // auto-close using our own clock, so server/client clock skew can't affect it.
  const seenAtRef = useRef(new Map());
  // A 1s tick so the auto-close re-evaluates without waiting for the next poll.
  const [nowTick, setNowTick] = useState(() => Date.now());

  useEffect(() => {
    const map = seenAtRef.current;
    liveCards.forEach((c) => {
      const k = cardKey(c);
      if (!map.has(k)) map.set(k, Date.now());
    });
  }, [liveCards]);

  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const visibleCards = useMemo(() => {
    return liveCards.filter((c) => {
      const k = cardKey(c);
      if (dismissed.has(k)) return false; // manually closed this trigger
      const seen = seenAtRef.current.get(k) ?? nowTick;
      return nowTick - seen < AUTO_CLOSE_MS; // auto-close after 30s
    });
  }, [liveCards, dismissed, nowTick]);

  const closeCard = (c) => {
    setDismissed((prev) => new Set(prev).add(cardKey(c)));
  };

  const canStart = Boolean(meetingId && joinUrl);

  const handleStart = async () => {
    if (!canStart) return;
    try {
      await startLiveAssist({ meetingId, joinUrl, organizerId }).unwrap();
      setStarted(true);
    } catch (e) {
      console.log("startLiveAssist failed", e);
    }
  };

  const handleStop = async () => {
    try {
      await stopLiveAssist({ meetingId }).unwrap();
    } catch (e) {
      console.log("stopLiveAssist failed", e);
    } finally {
      setStarted(false);
    }
  };

  return {
    started,
    canStart,
    starting,
    stopping,
    startError,
    handleStart,
    handleStop,
    visibleCards,
    closeCard,
  };
}
